<!-- src/views/admin/AdminEngineeringManageFormOptionsView.vue -->
<template>
  <div class="p-4 sm:p-6 md:p-8 bg-[#F0F0F0] min-h-screen font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="max-w-7xl mx-auto pb-24 text-left">

      <!-- HEADER & ACTIONS: Midnight Premium Standard -->
      <div class="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-12 gap-6 bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] animate-fadeIn">
        <div>
          <h1 class="text-3xl font-black text-white uppercase tracking-tight">Form Options Registry</h1>
          <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">Manage Engineering & Plant Options • Sentinel Safe Premium</p>
        </div>
        
        <div class="flex flex-wrap gap-3 w-full xl:w-auto items-center">
            <!-- 🛡️ NAVIGATION STANDARDISED: Uniform Return back button layout and matching vertical padding -->
            <router-link to="/admin/engineering" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
               <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
            </router-link>
        </div>
      </div>

      <!-- 🛡️ PERMISSIONS BLOCK: Guarded against unauthorised non-admin access -->
      <div v-if="!hasClearance && !loading" class="bg-rose-50 border-2 border-[#EF4444]/20 p-10 rounded-[40px] text-center text-[#EF4444] font-black uppercase tracking-widest text-xs animate-fadeIn">
        Access Denied: Administrative clearance is required to view this registry.
      </div>

      <template v-else>
        <div v-if="loading" class="flex flex-col items-center justify-center py-32">
          <div class="w-12 h-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full animate-spin"></div>
          <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mt-6">Decrypting Options Registry...</p>
        </div>
        
        <div v-else-if="error" class="bg-rose-50 border-2 border-[#EF4444]/20 p-10 rounded-[40px] text-center text-[#EF4444] font-black uppercase tracking-widest text-xs animate-fadeIn">
          {{ error }}
        </div>
        
        <div v-else class="space-y-10 animate-fadeIn">
          
          <!-- Initialisation Button (Only shows if data is missing) -->
          <div v-if="!hasData" class="bg-amber-50 border-2 border-amber-200/50 p-6 rounded-[32px] mb-8">
            <div class="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-tight">Database Registry Empty</h3>
                <p class="text-xs text-slate-500 font-medium mt-1">
                  No custom options found in the database. Click to populate default engineering and plant options (Regions, Disciplines, Plant Equipment Items, etc.).
                </p>
              </div>
              <button @click="initialiseDefaults" class="bg-[#0A1630] text-white px-6 py-3.5 rounded-xl hover:bg-slate-800 transition font-black uppercase text-[10px] tracking-widest shrink-0 border-none cursor-pointer shadow-md">
                Initialise Defaults
              </button>
            </div>
          </div>

          <!-- Loop through each FORM SECTION -->
          <div v-for="section in formSections" :key="section.title" class="bg-white p-8 rounded-[40px] border border-slate-100 shadow-2xl text-left">
            <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight mb-8 border-b pb-4 text-left">{{ section.title }}</h2>
            
            <div class="space-y-12 text-left">
              <!-- Loop through each OPTION GROUP within the section -->
              <div v-for="optionGroupId in section.optionGroupIds" :key="optionGroupId" class="text-left">
                <div v-if="optionsDataMap[optionGroupId]" class="text-left">
                  
                  <div class="flex items-center gap-4 mb-4 text-left">
                    <h3 class="text-base font-black text-[#0A1630] uppercase tracking-wider mb-0 text-left">{{ formatTitle(optionGroupId) }}</h3>
                    
                    <div v-if="optionsDataMap[optionGroupId].isClientSpecific" class="flex items-center gap-2">
                       <span class="inline-flex items-center px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-200/30">
                         Custom List (Client Specific)
                       </span>
                       <button @click="resetToGlobal(optionGroupId)" class="text-[9px] font-black text-[#FD5E00] uppercase tracking-widest hover:underline cursor-pointer border-none bg-transparent">Reset to Global Defaults</button>
                    </div>
                  </div>

                  <!-- List of existing options -->
                  <div class="space-y-2 mb-6 text-left max-w-3xl">
                    <div v-for="(option, index) in optionsDataMap[optionGroupId].options" :key="index" class="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-colors">
                      <div class="text-sm font-semibold flex items-center gap-3">
                        <span class="font-mono text-[9px] font-black text-slate-400 bg-slate-200/60 px-2 py-0.5 rounded-lg">{{ option.value }}</span>
                        <span class="text-slate-700 uppercase text-xs font-bold">{{ option.text }}</span>
                      </div>
                      <div class="flex-shrink-0 space-x-2">
                        <button @click="openEditModal(optionGroupId, option)" class="text-[#0A1630] hover:text-[#FD5E00] p-1 bg-white rounded-lg border border-slate-100 shadow-sm cursor-pointer" title="Edit Option">
                          <PencilIcon class="h-4 w-4" />
                        </button>
                        <button @click="deleteOption(optionGroupId, option)" class="text-rose-500 hover:text-rose-700 p-1 bg-white rounded-lg border border-slate-100 shadow-sm cursor-pointer" title="Delete Option">
                          <TrashIcon class="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p v-if="!optionsDataMap[optionGroupId].options || optionsDataMap[optionGroupId].options.length === 0" class="text-sm text-gray-500 italic px-2">No options defined yet.</p>
                  </div>

                  <!-- Form to add a new option -->
                  <form @submit.prevent="addOption(optionGroupId)" class="flex flex-col sm:flex-row items-end gap-4 border-t border-slate-50 pt-6 max-w-3xl text-left">
                    <div class="w-full sm:flex-grow text-left">
                      <label :for="'newValue-' + optionGroupId" class="micro-label mb-2 block ml-1 text-left">New Option Value (Asset ID / Slug)</label>
                      <input type="text" :id="'newValue-' + optionGroupId" v-model="newOptions[optionGroupId].value" placeholder="e.g., tec_eq_001" required class="form-input" />
                    </div>
                    <div class="w-full sm:flex-grow text-left">
                      <label :for="'newText-' + optionGroupId" class="micro-label mb-2 block ml-1 text-left">New Option Text (Description)</label>
                      <input type="text" :id="'newText-' + optionGroupId" v-model="newOptions[optionGroupId].text" placeholder="e.g., Trimble S9 Total Station" required class="form-input" />
                    </div>
                    <button type="submit" class="bg-[#0A1630] text-white py-4 px-8 rounded-2xl hover:bg-slate-800 transition-colors text-[10px] font-black uppercase tracking-widest flex-shrink-0 cursor-pointer border-none shadow-sm w-full sm:w-auto h-12 flex items-center justify-center">Add Option</button>
                  </form>

                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Edit Modal -->
  <TransitionRoot as="template" :show="isEditModalOpen">
    <Dialog as="div" class="relative z-[100]" @close="isEditModalOpen = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-[#0A1630]/90 backdrop-blur-md transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-[40px] bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg p-10 border border-slate-100">
              <form @submit.prevent="saveOptionEdit" class="space-y-8">
                <div class="bg-white">
                  <DialogTitle as="h3" class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none mb-6">Edit Option</DialogTitle>
                  <div class="space-y-6">
                     <div class="text-left">
                        <label for="editValue" class="micro-label mb-2 block ml-1 text-left">Option Value (Restricted)</label>
                        <input type="text" id="editValue" :value="editingOption.current?.value" disabled class="form-input bg-slate-100 text-slate-400 cursor-not-allowed" />
                    </div>
                    <div class="text-left">
                        <label for="editText" class="micro-label mb-2 block ml-1 text-left">Option Text (Display Name)</label>
                        <input type="text" id="editText" v-model="editingOption.newText" required class="form-input" />
                    </div>
                  </div>
                </div>
                <div class="pt-6 border-t border-slate-50 flex gap-4 text-center">
                  <button type="button" class="flex-1 py-4 bg-slate-100 text-slate-500 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all text-center border-none" @click="isEditModalOpen = false">Cancel</button>
                  <button type="submit" class="flex-1 py-4 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 active:scale-95 transition-all shadow-xl text-center border-none">Save Changes</button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, setDoc, deleteField } from 'firebase/firestore';
import { PencilIcon, TrashIcon, ArrowUturnLeftIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useUserStore } from '@/stores/userStore';

const userStore = useUserStore();
const loading = ref(true);
const error = ref(null);
const optionsDataMap = ref({});
const newOptions = ref({});

const formSections = ref([
  { title: '1. Support Request Form', optionGroupIds: ['region', 'discipline', 'resourceRequired'] },
  { title: '2. Plant & Equipment Register (MSPRG009)', optionGroupIds: ['plantEquipmentItems'] }
]);

const hasData = computed(() => {
    return Object.values(optionsDataMap.value).some(group => group.options && group.options.length > 0);
});

// Default Data for Initialisation
const defaultEngineeringOptions = {
    region: [
        { text: "Kent", value: "kent" },
        { text: "Sussex", value: "sussex" },
        { text: "Wessex", value: "wessex" }
    ],
    discipline: [
        { text: "Buildings", value: "buildings" },
        { text: "Drainage", value: "drainage" },
        { text: "Earthworks", value: "earthworks" },
        { text: "Structures", value: "structures" }
    ],
    resourceRequired: [
        { text: "CRE", value: "cre" },
        { text: "Engineer", value: "engineer" },
        { text: "Other", value: "other" },
        { text: "Stresser", value: "stresser" },
        { text: "Surveyor", value: "surveyor" },
        { text: "TQS", value: "tqs" },
        { text: "Track Hand Back", value: "track_hand_back" },
        { text: "Trackman", value: "trackman" }
    ],
    plantEquipmentItems: [
        { text: "Trimble S9 Total Station", value: "tec_eq_001" },
        { text: "Live Line Tester", value: "tec_eq_010" }
    ]
};

const isEditModalOpen = ref(false);
const editingOption = reactive({
  groupId: null,
  current: null,
  newText: ''
});

const formatTitle = (id) => {
  if (!id) return '';
  const result = id.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

// 🛡️ RECONCILED IDENTITY RESOLUTION: Secure reactive client tracking prevents race condition crashes
const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

// 🛡️ SIMPLIFIED ADMINISTRATIVE CLEARANCE: Bypasses token-mismatch blockages for Super Admins and local Admins with Command Centre access
const hasClearance = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.hasAdminAccess ||
         userStore.permissions?.canViewEngineering === true ||
         userStore.permissions?.canManageEngineering === true;
});

const fetchOptions = async () => {
  const clientId = currentClientId.value;
  
  // Guard against missing clientId during initial hydration to prevent infinite spooling
  if (!clientId) {
    loading.value = true;
    setTimeout(() => {
      if (!currentClientId.value) {
        error.value = "Identity context missing. Please verify your client workspace selection.";
        loading.value = false;
      }
    }, 5000);
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const allGroupIds = formSections.value.flatMap(section => section.optionGroupIds);
    const uniqueGroupIds = [...new Set(allGroupIds)];

    const dataMap = {};
    const newOpts = {};

    for (const id of uniqueGroupIds) {
      newOpts[id] = { value: '', text: '' };
      const docRef = doc(db, 'formOptions', id);
      const docSnap = await getDoc(docRef);

      let optionsToShow = [];
      let isClientSpecific = false;

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data[clientId] && Array.isArray(data[clientId])) {
          optionsToShow = data[clientId];
          isClientSpecific = true;
        } else if (data.global && Array.isArray(data.global)) {
          optionsToShow = data.global;
        }
      }

      // Sort alphabetically
      optionsToShow.sort((a, b) => (a.text || '').localeCompare(b.text || ''));
      dataMap[id] = { id, options: optionsToShow, isClientSpecific };
    }

    optionsDataMap.value = dataMap;
    newOptions.value = newOpts;

  } catch (err) {
    console.error("Error fetching form options:", err);
    error.value = `Failed to load options: ${err.message}`;
  } finally {
    loading.value = false;
  }
};

const initialiseDefaults = async () => {
    if(!confirm("This will populate the database with standard default options for engineering and plant items. Continue?")) return;
    loading.value = true;
    try {
        for (const [key, options] of Object.entries(defaultEngineeringOptions)) {
            const docRef = doc(db, 'formOptions', key);
            await setDoc(docRef, { global: options }, { merge: true });
        }
        await fetchOptions();
        alert("Defaults initialised successfully.");
    } catch (e) {
        console.error(e);
        alert("Failed to initialise defaults: " + e.message);
    } finally {
        loading.value = false;
    }
};

const resetToGlobal = async (groupId) => {
    const clientId = currentClientId.value;
    if (!clientId) return;
    if (!confirm("Are you sure? This will delete all custom options for this list and revert to the global defaults.")) return;
    
    try {
        const docRef = doc(db, 'formOptions', groupId);
        await updateDoc(docRef, {
            [clientId]: deleteField()
        });
        await fetchOptions();
    } catch (e) {
        console.error(e);
        alert("Failed to reset: " + e.message);
    }
};

const addOption = async (groupId) => {
  const clientId = currentClientId.value;
  const newOpt = newOptions.value[groupId];
  if (!newOpt.value || !newOpt.text || !clientId) {
    alert("Both value and text fields are required.");
    return;
  }
  
  const docRef = doc(db, 'formOptions', groupId);
  const payload = { value: newOpt.value.toLowerCase().replace(/\s+/g, '_').trim(), text: newOpt.text.trim() };
  
  try {
    const group = optionsDataMap.value[groupId];
    if (!group.isClientSpecific) {
        const newClientOptions = [...group.options, payload];
        await setDoc(docRef, { [clientId]: newClientOptions }, { merge: true });
    } else {
        await updateDoc(docRef, {
            [clientId]: arrayUnion(payload)
        });
    }

    newOptions.value[groupId] = { value: '', text: '' };
    await fetchOptions();
  } catch(e) {
    console.error(e);
    alert('Error adding option: ' + e.message);
  }
};

const deleteOption = async (groupId, optionToDelete) => {
  const clientId = currentClientId.value;
  const group = optionsDataMap.value[groupId];
  if (!clientId) return;
  if (!confirm(`Are you sure you want to delete the option "${optionToDelete.text}"?`)) return;
  
  const docRef = doc(db, 'formOptions', groupId);
  try {
    if (!group.isClientSpecific) {
        const newClientOptions = group.options.filter(opt => opt.value !== optionToDelete.value);
        await setDoc(docRef, { [clientId]: newClientOptions }, { merge: true });
    } else {
        await updateDoc(docRef, {
          [clientId]: arrayRemove(optionToDelete)
        });
    }
    await fetchOptions();
  } catch(e) {
     console.error(e);
     alert('Error deleting option: ' + e.message);
  }
};

const saveOptionEdit = async () => {
  const clientId = currentClientId.value;
  const { groupId, current, newText } = editingOption;
  const group = optionsDataMap.value[groupId];
  if (!clientId || !groupId) return;

  const docRef = doc(db, 'formOptions', groupId);
  const updatedOptions = group.options.map(opt => 
    opt.value === current.value ? { ...opt, text: newText } : opt
  );
  
  try {
    await setDoc(docRef, { [clientId]: updatedOptions }, { merge: true });
    isEditModalOpen.value = false;
    await fetchOptions();
  } catch(e) {
    console.error(e);
    alert('Error saving changes: ' + e.message);
  }
};

const openEditModal = (groupId, option) => {
    editingOption.groupId = groupId;
    editingOption.current = option;
    editingOption.newText = option.text;
    isEditModalOpen.value = true;
};

// 🛡️ RECONCILED SYNC: Dual trigger (watch & onMounted fallback) ensures seamless hydration for Super Admins
watch(currentClientId, (newId) => {
  if (newId) {
    fetchOptions();
  }
}, { immediate: true });

onMounted(() => {
  if (currentClientId.value) {
    fetchOptions();
  }
});
</script>

<style scoped>
.micro-label { @apply text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }
.form-input { @apply block w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-black uppercase transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none shadow-inner; }
.font-mono { 
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #20FCF1; }
</style>